import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, Store, BadgePercent, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CartSheet } from "@/components/CartSheet";
import { BrandMarquee } from "@/components/BrandMarquee";
import { FeaturedCarousel } from "@/components/FeaturedCarousel";
import { ReelGallery } from "@/components/ReelGallery";
import { BestSellersReels } from "@/components/BestSellersReels";
import { HeroReels } from "@/components/HeroReels";
import { PinoSection } from "@/components/PinoSection";
import { ProjectSection } from "@/components/ProjectSection";
import { GalleryProjects } from "@/components/GalleryProjects";
import { QuoteSection } from "@/components/QuoteSection";
import { Reveal } from "@/components/Reveal";
import { ProductImage } from "@/components/ProductImage";
import { still, categoryCover } from "@/lib/media";
import { PAYMENT_METHODS } from "@/lib/site";

const FEATURES = [
    { icon: Truck, t: "Envío nacional", d: "A toda Venezuela" },
    { icon: Store, t: "Retiro en tienda", d: "Rápido y seguro" },
    { icon: BadgePercent, t: "Detal y mayoreo", d: "Precios por volumen" },
    { icon: ShieldCheck, t: "Producto nuevo", d: "Sellado y garantizado" },
];

const CHAPTERS = [
    { n: "01", t: "Pino nacional", d: "Madera de producción venezolana, seleccionada y lista para trabajar." },
    { n: "02", t: "Herrajes que rinden", d: "Bisagras, correderas, cerraduras y tiradores de marcas líderes." },
    { n: "03", t: "Para tu taller", d: "Compra una pieza o abastece tu negocio con precio de mayoreo." },
];

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [categories, setCategories] = useState([]);
    const heroRef = useRef(null);

    useEffect(() => {
        api.get("/products", { params: { destacado: true, limit: 12 } }).then((r) => setFeatured(r.data.items)).catch(() => {});
        api.get("/products/categories").then((r) => setCategories(r.data)).catch(() => {});
    }, []);

    const topCats = categories.slice(0, 12);

    return (
        <div className="bg-crema">
            <Navbar overHero />
            <CartSheet />

            {/* HERO */}
            <section ref={heroRef} className="relative h-[92vh] min-h-[620px] overflow-hidden">
                <HeroReels />

                <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16 md:pb-24">
                    <motion.span
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                        className="text-xs uppercase tracking-[0.25em] font-semibold text-madera mb-5"
                    >
                        Pino nacional · Herrajes · Todo para el carpintero
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7 }}
                        className="font-display font-extrabold text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl"
                    >
                        La madera y el herraje que tu proyecto merece.
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                        className="text-lg text-white/80 max-w-xl mt-6"
                    >
                        Más de 2.000 referencias en stock. Al detal y al mayor, con envío a toda Venezuela y retiro en tienda.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
                        className="flex flex-wrap gap-3 mt-9"
                    >
                        <Link to="/tienda" data-testid="hero-shop-btn" className="group inline-flex items-center gap-2.5 bg-naranja text-white rounded-full px-8 py-4 font-semibold hover:bg-naranja-dark transition-colors shadow-lift">
                            Explorar tienda <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <a href="#mayoreo" className="inline-flex items-center rounded-full px-8 py-4 font-semibold text-white border-2 border-white/40 hover:border-madera hover:text-madera transition-colors">
                            Comprar al mayor
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* FEATURES */}
            <section className="border-b border-linea bg-hueso">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-linea">
                    {FEATURES.map((f) => (
                        <div key={f.t} className="flex items-center gap-3 py-6 px-4 first:pl-0">
                            <f.icon size={26} className="text-verde shrink-0" strokeWidth={1.6} />
                            <div>
                                <p className="font-display font-semibold text-ink text-sm">{f.t}</p>
                                <p className="text-xs text-mut">{f.d}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* PINO NACIONAL */}
            <PinoSection />

            {/* FEATURED */}
            <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal>
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                        <div>
                            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">Selección de la casa</span>
                            <h2 className="font-display font-bold text-3xl md:text-5xl text-ink mt-2 tracking-tight">Productos destacados</h2>
                        </div>
                        <Link to="/tienda" className="group inline-flex items-center gap-2 text-verde font-semibold hover:text-naranja transition-colors">
                            Ver todo el catálogo <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                </Reveal>
                {featured.length > 0 && <FeaturedCarousel products={featured} />}
            </section>

            {/* PAQUETES Y DISEÑO · las dos puertas de venta grande, en el camino
                del que ya vio los productos y está buscando qué hacer con ellos */}
            <section id="paquetes" className="py-16 md:py-24 bg-hueso border-y border-linea">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="mb-10">
                            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">No compres piezas sueltas</span>
                            <h2 className="font-display font-bold text-3xl md:text-5xl text-ink mt-2 tracking-tight">¿Qué vas a construir?</h2>
                            <p className="text-mut mt-3 max-w-2xl text-lg">Te armamos todo lo que hace falta para terminar el trabajo, con los precios del día. Y si todavía no sabes cómo lo quieres, te lo diseñamos.</p>
                        </div>
                    </Reveal>

                    <div className="grid gap-5 md:grid-cols-2">
                        <Reveal>
                            <a href="/combos" data-testid="home-paquetes" className="group block h-full rounded-2xl overflow-hidden border border-linea bg-crema hover:border-verde transition-colors shadow-soft">
                                <img
                                    src="https://api.laromanilla.com/api/products/image/maderas-la-rola/piezas/cocina.jpg"
                                    alt="Paquetes por tipo de trabajo"
                                    className="w-full aspect-[16/10] object-cover"
                                    loading="lazy"
                                />
                                <div className="p-6 md:p-7">
                                    <span className="text-xs uppercase tracking-[0.18em] font-bold text-naranja">15 paquetes</span>
                                    <h3 className="font-display font-bold text-2xl md:text-3xl text-ink mt-2">Paquetes por trabajo</h3>
                                    <p className="text-mut mt-2.5">Mueble de cocina, closet, puerta, acabados, obra seca. Cada uno con su lista completa y su precio, para que no pares el trabajo buscando la pieza que falta.</p>
                                    <span className="mt-4 inline-flex items-center gap-2 text-verde font-semibold group-hover:text-naranja transition-colors">
                                        Ver los paquetes <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </div>
                            </a>
                        </Reveal>

                        <Reveal delay={0.08}>
                            <a href="/diseno" data-testid="home-diseno" className="group block h-full rounded-2xl overflow-hidden border border-linea bg-crema hover:border-verde transition-colors shadow-soft">
                                <img
                                    src="https://api.laromanilla.com/api/products/image/maderas-la-rola/diseno/propuesta-2.jpg"
                                    alt="Diseño de tu proyecto en 3D"
                                    className="w-full aspect-[16/10] object-cover"
                                    loading="lazy"
                                />
                                <div className="p-6 md:p-7">
                                    <span className="text-xs uppercase tracking-[0.18em] font-bold text-naranja">Nuevo · te asesoramos</span>
                                    <h3 className="font-display font-bold text-2xl md:text-3xl text-ink mt-2">Te diseñamos el proyecto</h3>
                                    <p className="text-mut mt-2.5">Mándanos fotos y medidas del espacio y te devolvemos tres propuestas en 3D con la lista de materiales y su precio. Para carpinteros y para tu casa, en todo el país.</p>
                                    <span className="mt-4 inline-flex items-center gap-2 text-verde font-semibold group-hover:text-naranja transition-colors">
                                        Cómo funciona <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </div>
                            </a>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* MÁS VENDIDOS EN VIDEO */}
            <section id="mas-vendidos" className="py-16 md:py-24 bg-hueso border-y border-linea">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">Lo que más se lleva la gente</span>
                        <h2 className="font-display font-bold text-3xl md:text-5xl text-ink mt-2 mb-3 tracking-tight">Más vendidos</h2>
                        <p className="text-mut mb-10 max-w-xl">Toca cualquier producto para ver su video. Todos disponibles en tienda, al detal y al mayor.</p>
                    </Reveal>
                    <BestSellersReels />
                </div>
            </section>

            {/* PRESUPUESTO */}
            <QuoteSection />

            {/* CATEGORIES */}
            <section id="categorias" className="py-16 md:py-24 bg-hueso border-y border-linea">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">Explora por categoría</span>
                        <h2 className="font-display font-bold text-3xl md:text-5xl text-ink mt-2 mb-10 tracking-tight">Todo el taller en un lugar</h2>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {topCats.map((c, i) => (
                            <Reveal key={c.nombre} delay={(i % 4) * 0.05}>
                                <Link
                                    to={`/tienda?categoria=${encodeURIComponent(c.nombre)}`}
                                    data-testid={`category-${i}`}
                                    className="group relative block aspect-[4/3] rounded-2xl overflow-hidden shadow-soft bg-hueso"
                                >
                                    <img src={c.foto || categoryCover(c.nombre)} alt={c.nombre} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-verde-900/90 via-verde-900/30 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-4">
                                        <p className="text-[11px] text-madera font-medium">{c.count} productos</p>
                                        <h3 className="font-display font-bold text-white text-lg leading-tight mt-0.5">{c.nombre}</h3>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* MANIFESTO */}
            <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-5">
                        <Reveal>
                            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-lift">
                                <img src={still("eligiendo")} alt="Asesoría en tienda" className="w-full h-full object-cover" />
                            </div>
                        </Reveal>
                    </div>
                    <div className="md:col-span-6 md:col-start-7">
                        <Reveal>
                            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">Nuestra casa</span>
                            <h2 className="font-display font-bold text-3xl md:text-5xl text-ink mt-2 tracking-tight leading-tight">Hecho para durar, hecho en Venezuela.</h2>
                        </Reveal>
                        <div className="mt-8 space-y-1">
                            {CHAPTERS.map((c, i) => (
                                <Reveal key={c.n} delay={i * 0.08}>
                                    <div className="flex gap-5 py-5 border-t border-linea">
                                        <span className="font-display font-extrabold text-3xl md:text-4xl text-madera shrink-0">{c.n}</span>
                                        <div>
                                            <h3 className="font-display font-semibold text-xl text-ink">{c.t}</h3>
                                            <p className="text-mut mt-1 leading-relaxed">{c.d}</p>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* REELS */}
            <section className="py-16 md:py-24 bg-hueso border-y border-linea">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">En video</span>
                        <h2 className="font-display font-bold text-3xl md:text-5xl text-ink mt-2 mb-3 tracking-tight">Recorre nuestras secciones</h2>
                        <p className="text-mut mb-10 max-w-xl">Pasa el cursor o toca para reproducir. Del taller a la cocina, tenemos todo lo que buscas.</p>
                    </Reveal>
                    <ReelGallery />
                </div>
            </section>

            {/* PROYECTOS INTERNACIONALES */}
            <GalleryProjects />
            <ProjectSection />

            {/* MAYOREO */}
            <section id="mayoreo" className="relative overflow-hidden hero-gradient text-white">
                <img src={still("entrega")} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7">
                        <Reveal>
                            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">Para tu taller o negocio</span>
                            <h2 className="font-display font-extrabold text-4xl md:text-6xl mt-3 tracking-tight">Compra al mayor y ahorra</h2>
                            <p className="text-lg text-white/75 max-w-xl mt-6">
                                Al agregar <strong className="text-madera">12 o más unidades</strong> de un mismo producto, el precio de mayoreo se aplica automáticamente en tu carrito.
                            </p>
                            <Link to="/tienda" className="mt-8 inline-flex items-center gap-2 bg-naranja text-white rounded-full px-8 py-4 font-semibold hover:bg-naranja-dark transition-colors">
                                Ir al catálogo <ArrowRight size={18} />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="md:col-span-4 md:col-start-9">
                        <Reveal delay={0.15}>
                            <div className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-8 text-center">
                                <p className="font-display text-7xl font-extrabold text-madera leading-none">−12%</p>
                                <p className="text-sm text-white/70 mt-3 uppercase tracking-widest">Descuento por volumen</p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* PAYMENTS */}
            <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal>
                    <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">Múltiples formas de pago</span>
                    <h2 className="font-display font-bold text-3xl md:text-5xl text-ink mt-2 mb-10 tracking-tight">Paga como prefieras</h2>
                </Reveal>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {PAYMENT_METHODS.map((m, i) => (
                        <Reveal key={m.id} delay={i * 0.05}>
                            <div className="bg-hueso border border-linea rounded-2xl p-6 h-full shadow-soft hover:shadow-lift hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
                                <span className="text-xs text-madera font-semibold">0{i + 1}</span>
                                <h3 className="font-display font-bold text-lg text-ink mt-3">{m.label}</h3>
                                <p className="text-xs text-mut mt-1">{m.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <BrandMarquee />
            <Footer />
        </div>
    );
}
